<template>
    <div>
        <div class="col-md-12 mb-2">
            <form class="availability-form" ref="form">
                <input type="hidden" name="_token" :value="csrfToken">
                <div>
                    <div class="row" v-if="isEditing">
                        <div class="col-md-2 form-group">
                            <label class="font-weight-bold">Tipo</label><br>
                            <span>{{ typeLabel(typeValue) }}</span>
                        </div>
                        <div class="col-md-2 form-group">
                            <label class="font-weight-bold">Data</label><br>
                            <span>{{ formatDateDisplay(availability.applies_at) }}</span>
                        </div>
                        <div class="col-md-2 form-group">
                            <label class="font-weight-bold">Dia</label><br>
                            <span class="text-capitalize">{{ formatWeekdayDisplay(availability.applies_at) }}</span>
                        </div>
                        <div class="col-md-2 form-group">
                            <label class="font-weight-bold">Horário</label><br>
                            <span class="text-capitalize">{{ formatTimeDisplay(availability.from_minutes, availability.to_minutes) }}</span>
                        </div>
                        <div v-if="shouldDisplayOccupationQuantity" class="col-md-2 form-group">
                            <label class="font-weight-bold">Vendidas</label><br>
                            <span class="text-capitalize">{{ availability.date_occupation_quantity }}</span>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-6 col-lg-6 col-xl-3 form-group" v-if="isCreating">
                            <label for="type">Tipo</label>

                            <select class="form-control" v-model="typeValue" required name="type" id="type">
                                <option v-for="type in typeOptions" :key="type.code" :value="type.code">{{ type.label }}</option>
                            </select>

                            <input v-if="isEditing" id="type" class="form-control" disabled="true" :value="typeLabel(typeValue)" />
                            <span class="form-error">{{ validationMessage('type') }}</span>
                        </div>

                        <div class="col-md-6 col-lg-6 col-xl-3 form-group">
                            <label for="quantity">Quantidade</label>
                            <input type="number" v-model="quantityValue" min="0" class="form-control" name="quantity" id="quantity" required>
                            <span class="form-error">{{ validationMessage('quantity') }}</span>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-md-6 col-lg-6 col-xl-3 form-group" v-if="isCreating">
                            <label for="begin_date">Data de início</label>
                            <DatepickerInput name="begin_date" :default-date="beginDate" :on-selection="handleBeginDateSelection" />
                            <span class="form-error">{{ validationMessage('begin_date') }}</span>
                        </div>

                        <div class="col-md-6 col-lg-6 col-xl-3 form-group" v-if="isCreating">
                            <label for="end_date">Data de término</label>
                            <DatepickerInput name="end_date" :default-date="endDate" :on-selection="handleEndDateSelection" />
                            <span class="form-error">{{ validationMessage('end_date') }}</span>
                        </div>

                        <div class="col-md-6 col-lg-6 col-xl-3 form-group" v-if="isCreating">
                            <label for="fromMinutes">Horário de início</label>
                            <select class="form-control" v-model="fromMinutes" name="from_minutes" id="fromMinutes" :disabled="!canSelectTime">
                                <option v-for="option in minuteSetOptions" :value="option.value" :key="`fromMinutes-${option.value}`">{{ option.label }}</option>
                            </select>
                            <span class="form-error">{{ validationMessage('from_minutes') }}</span>
                        </div>

                        <div class="col-md-6 col-lg-6 col-xl-3 form-group" v-if="isCreating">
                            <label for="toMinutes">Horário de término</label>
                            <select class="form-control" v-model="toMinutes" name="to_minutes" id="toMinutes" :disabled="!canSelectTime">
                                <option v-for="option in minuteSetOptions" :value="option.value" :key="`toMinutes-${option.value}`">{{ option.label }}</option>
                            </select>
                            <span class="form-error">{{ validationMessage('to_minutes') }}</span>
                        </div>

                        <div class="col-md-5 form-group btn-group btn-group" v-if="isEditing">
                            <button class="btn btn-outline-primary btn-action" v-on:click.prevent="triggerDelete()">Excluir</button>
                            <button class="btn btn-outline-primary btn-action" v-on:click.prevent="triggerDelete(true)" v-if="availabilityHasSiblings">Excluir similares</button>
                        </div>

                        <div class="col-md-4 form-group btn-group text-right" v-if="isEditing && availabilityHasSiblings">
                            <button v-on:click.prevent="triggerSubmit(true)" class="btn btn-outline-primary btn-action">Alterar similares</button>
                        </div>
                    </div>

                    <div v-if="!canSelectTime">
                        <div class="alert alert-info" role="info">
                            <strong>Atenção:</strong> Não será possível definir horários da disponibilidade para a seleção. Isso ocorre pois um ou mais produtos selecionados não permitem a venda com data e horário.
                        </div>
                    </div>

                    <div class="row" v-if="isCreating">
                        <div class="col" v-if="typeIsPerProducts">
                            <div v-for="(productGroup, index) in productGroups" :key="index" class="row">
                                <div v-for="product in productGroup" :key="product.id" class="col-md-3">
                                    <label>
                                        <input type="checkbox" v-model="selectedProductIds" name="related_ids[]" :value="product.id" @change="optionsChanged" />
                                        <span>{{ product.name }}</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div class="col" v-if="typeIsPerTariffs">
                            <div v-for="prod in productTariffGroups" :key="prod.id">
                                <h2 @click="toggleSelectedRelatedId('tariffs', prod.id)">{{ prod.name }}</h2>
                                <div class="row">
                                    <div class="col-md-3" v-for="tariffGroup in prod.tariffGroups" :key="tariffGroup.id">
                                        <div class="card overflow-auto mt-2 mb-4">
                                            <div class="card-body">
                                                <h5 class="card-title">{{ tariffGroup.name }}</h5>
                                                <div class="prod-group-item-list" v-if="relatedIdIsSelected('tariffs', prod.id)">
                                                    <div v-for="tariff in tariffGroup.tariffs" :key="tariff.id">
                                                        <label>
                                                            <input type="checkbox" v-model="selectedTariffIds" name="related_ids[]" :value="tariff.id" @change="optionsChanged" />
                                                            <span>{{ tariff.formatted_name }}</span>
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="col" v-if="typeIsPerProductTariffTypes">
                            <div v-for="prod in productTariffTypes" :key="prod.id">
                                <h2 @click="toggleSelectedRelatedId('tariff_types', prod.id)">{{ prod.name }}</h2>
                                <div class="prod-group-item-list" v-if="relatedIdIsSelected('tariff_types', prod.id)">
                                    <div v-for="(tariffTypeGroup, index) in prod.tariffTypeGroups" :key="index" class="row">
                                        <div v-for="tariffType in tariffTypeGroup" :key="tariffType.id" class="col-md-3">
                                            <label>
                                                <input type="checkbox" v-model="selectedTariffTypeIds" name="related_ids[]" :value="[tariffType.id, prod.id].join(',')" @change="optionsChanged"/>
                                                <span>{{ tariffType.name }}</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="col" v-if="typeIsPerAdditionals">
                            <div v-for="product in productAdditionalGroups" :key="product.id">
                                <h2 @click="toggleSelectedRelatedId('additionals', product.id)">{{ product.name }}</h2>
                                <div class="prod-group-item-list" v-if="relatedIdIsSelected('additionals', product.id)">
                                    <div v-for="(additionalGroup, index) in product.additionalGroups" :key="index" class="row">
                                        <div v-for="add in additionalGroup" :key="add.id" class="col-md-3">
                                            <label>
                                                <input type="checkbox" v-model="selectedAdditionalIds" name="related_ids[]" :value="add.id" @change="optionsChanged" />
                                                <span>{{ add.title }}</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
        <div class="col-md-12">
            <div class="form-group text-right">
                <button v-on:click="onCancel" class="btn btn-outline-primary btn-action px-5 mr-3">Cancelar</button>
                <button v-on:click="triggerSubmit()" class="btn btn-primary btn-action px-5">Salvar</button>
            </div>
        </div>
    </div>
</template>

<script>
import DatepickerInput from './DatepickerInput';
import { csrfToken, groupArray, minToHour } from '../helpers';
import moment from 'moment';

const PER_APP = 'per_app';
const PER_PRODUCTS = 'per_products';
const PER_TARIFFS = 'per_tariffs';
const PER_PRODUCT_TARIFF_TYPES = 'per_product_tariff_types';
const PER_ADDITIONALS = 'per_additionals';

const typeOptions = [
    { code: PER_APP, label: 'Geral' },
    { code: PER_PRODUCTS, label: 'Produto' },
    { code: PER_TARIFFS, label: 'Tarifa' },
    { code: PER_PRODUCT_TARIFF_TYPES, label: 'Tipos de Tarifa' },
    { code: PER_ADDITIONALS, label: 'Adicional' }
];

export default {
    props: {
        products: Array,
        availability: Object,
        onSubmit: Function,
        onCancel: Function,
        onDelete: Function,
        validation: null,
        defaultBeginDate: Object,
        defaultEndDate: Object,
        appMinuteSetInterval: String,
    },

    components: {
        DatepickerInput
    },

    data(){
        return {
            typeOptions,
            cachedAvailabilityId: null,
            typeValue: typeOptions[0].code,
            quantityValue: 10,
            selectedRelatedIds: {},
            beginDate: this.defaultBeginDate,
            endDate: this.defaultEndDate,
            fromMinutes: undefined,
            toMinutes: undefined,
            selectedProductIds: [],
            selectedAdditionalIds: [],
            selectedTariffIds: [],
            selectedTariffTypeIds: []
        };
    },

    computed: {
        csrfToken(){
            return csrfToken();
        },
        typeIsPerProducts(){
            return this.typeValue == PER_PRODUCTS;
        },
        typeIsPerTariffs(){
            return this.typeValue == PER_TARIFFS;
        },
        typeIsPerProductTariffTypes(){
            return this.typeValue == PER_PRODUCT_TARIFF_TYPES;
        },
        typeIsPerAdditionals(){
            return this.typeValue == PER_ADDITIONALS;
        },
        productGroups(){
            return groupArray(this.products, 4);
        },
        productTariffGroups(){
            const items = [];
            for(let prod of this.products){
                if(prod.tariffs.length == 0){
                    continue;
                }

                items.push({
                    id: prod.id,
                    name: prod.name,
                    tariffGroups: prod.tariff_groups
                });
            }

            return items;
        },
        productTariffTypes(){
            const items = [];
            for(let prod of this.products){
                items.push({
                    id: prod.id,
                    name: prod.name,
                    tariffTypeGroups: groupArray(prod.product_tariff_types, 4)
                });
            }
            return items;
        },
        productAdditionalGroups(){
            return this.products.filter(p => p.additionals.length > 0)
                .map(({ id, name, additionals }) => ({ id, name, additionalGroups: groupArray(additionals, 4) }));
        },
        availabilityHasSiblings(){
            return this.availability.has_siblings;
        },
        isCreating(){
            return this.availability === null;
        },
        isEditing(){
            return !this.isCreating;
        },
        shouldDisplayOccupationQuantity(){
            return this.availability.date_occupation_quantity !== null;
        },
        minuteSetOptions(){
            const options = [];
            const interval = parseInt(this.appMinuteSetInterval);

            for(let i = 0; i <= 1440; i += interval){
                options.push({ value: i, label: minToHour(i) });
            }

            return options;
        },
        canSelectTime(){
            let products = []
            switch (this.typeValue) {
                case PER_APP:
                    break;
                case PER_PRODUCTS:
                    products = this.selectedProductIds.map(productId => this.getProduct(productId));
                    break;
                case PER_TARIFFS:
                    products = this.selectedTariffIds.map(tariffId => this.getTariffProduct(tariffId));
                    break;
                case PER_PRODUCT_TARIFF_TYPES:
                    const productIds = this.selectedTariffTypeIds.map(tariffTypeId => tariffTypeId.split(',')[1]);
                    products = productIds.map(productId => this.getProduct(productId));
                    break;
                case PER_ADDITIONALS:
                    products = this.selectedAdditionalIds.map(additionalId => this.getAdditionalProduct(additionalId));
                    break;
                default:
                    break;
            }
            
            if (products.length){
                return !products.some(p => p.selling_mode !== 'with_date_and_time')
            }

            return true
        },
    },

    methods: {
        formatTimeDisplay(from, to){
            return `${minToHour(from)} - ${minToHour(to)}`;
        },
        optionsChanged(){
            if (!this.canSelectTime){
                this.fromMinutes = this.minuteSetOptions[0].value;
                this.toMinutes = this.minuteSetOptions[this.minuteSetOptions.length -1].value;
            }
        },
        typeLabel(code){
            for(let type of this.typeOptions){
                if(type.code === code)
                    return type.label;
            }

            return null;
        },

        triggerDelete(all = false){
            const question = all ? 'Tem certeza que deseja excluir todas as disponibilidades?' : 'Tem certeza que deseja excluir esta disponibilidade?';
            if(confirm(question)){
                this.onDelete(all);
            }
        },
        triggerSubmit(all = false){
            const form = this.$refs.form;

            const selects = form.querySelectorAll('#fromMinutes, #toMinutes');
            selects.forEach(function(input) {
                input.disabled = false;
            });

            const body = new FormData(form);

            if (all) {
                if(!confirm('Tem certeza que deseja alterar todas as disponibilidades?')){
                    return false;
                }

                body.append('update_clones', true);
            }

            this.handleSubmit(body);
        },
        handleSubmit(body){
            this.onSubmit(body);
        },
        validationMessage(key){
            const { validation } = this;
            return validation && validation[key] ? validation[key].join(', ') : '';
        },

        toggleSelectedRelatedId(prefix, id){
            const key = this.mountSelectedIdKey(prefix, id);
            this.selectedRelatedIds = { ...this.selectedRelatedIds, [key]: !this.relatedIdIsSelected(prefix, id) };
        },
        relatedIdIsSelected(prefix, id){
            const key = this.mountSelectedIdKey(prefix, id);
            const value = this.selectedRelatedIds[key];
            return value || value == null;
        },
        mountSelectedIdKey(prefix, id){
            return `${prefix}-${id}`;
        },

        fillValues(){
            const id = this.availability ? this.availability.id : null;
            if(id !== this.cachedAvailabilityId && id != null){
                this.cachedAvailabilityId = id;

                this.typeValue = this.availability.type;
                this.quantityValue = this.availability.quantity;
                this.fromMinutes = this.availability.from_minutes;
                this.toMinutes = this.availability.to_minutes;
            }
        },

        handleBeginDateSelection(date){
            this.beginDate = date;
        },
        handleEndDateSelection(date){
            this.endDate = date;
        },
        formatDateDisplay(date){
            return moment(String(date)).format('DD/MM/YYYY');
        },
        formatWeekdayDisplay(date){
            moment.locale('pt-br');
            return moment(String(date)).format('dddd');
        },
        getProduct(id){
            return this.products.find(p => p.id == id);
        },
        getTariffProduct(id){
            return this.products.find(p => p.tariffs.some(t => t.id == id));
        },
        getAdditionalProduct(id){
            return this.products.find(p => p.additionals.some(a => a.id == id));
        },
    },

    mounted(){
        this.fillValues();

        if (this.isCreating){
            this.fromMinutes = this.minuteSetOptions[0].value;
            this.toMinutes = this.minuteSetOptions[this.minuteSetOptions.length -1].value;
        }
    },
    updated(){
        this.fillValues();
    },
    watch: {
        typeValue(){
            this.selectedProductIds = [];
            this.selectedAdditionalIds = [];
            this.selectedTariffIds = [];
            this.selectedTariffTypeIds = [];
        },
    }
}
</script>

<style scoped>
.availability-form .container {
    padding: 16px;
    border-radius: 10px;
    background-color: #efefef;
}

.btn-group{
    align-self: end;
    display: table;
}

.btn-action{
    min-height: 38px;
    padding: 0 25px;
}

.card{
    max-height: 235px;
}

.card-title{
    font-size: 1rem;
    font-weight: 600;
}

.form-error {
    color: #ea4335;
}

.prod-group-item-list {
    font-size: .8rem;
}
</style>
